(ns ductile.ui.deframe
  "Simple in-browser state storage and effect system.

  Currently implemented using re-frame."
  (:refer-clojure :exclude [assoc! dissoc!])
  (:require ["@bugsnag/js" :as bugsnag]
            [clojure.edn :as edn]
            [clojure.set :as set]
            [ductile.base64u :as base64]
            [ductile.ui.deframe :as-alias deframe]
            [ductile.ui.http :as http]
            [ductile.ui.locale :as locale]
            [editscript.core :as editscript]
            [nextjournal.clerk.render.localstorage :as localstorage]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [reagent.ratom :as ratom]))

(rf/reg-sub
 ::get-in
 (fn [db [_q-id path not-found]]
   (get-in db path not-found)))

(rf/reg-sub
 ::get
 (fn  [db [_q-id k not-found]]
   (get db k not-found)))

(defn !get
  ([k] (rf/subscribe [::get k]))
  ([k not-found] (rf/subscribe [::get k not-found])))

(defn !get-in
  ([path] (rf/subscribe [::get-in path]))
  ([path not-found] (rf/subscribe [::get-in path not-found])))

(defn !localstorage-get
  "A subscription for state persisted in localstorage"
  ([k] (rf/subscribe [::get-in [::localstorage k]]))
  ([k not-found] (rf/subscribe [::get-in [::localstorage k] not-found])))

(defn !localstorage-get-in
  "A subscription for state persisted in localstorage"
  ([path] (rf/subscribe [::get-in (into [::localstorage] path)]))
  ([path not-found] (rf/subscribe [::get-in (into [::localstorage] path) not-found])))

(defn !route-get
  "A subscription for transient state storage, will be cleared on navigation."
  ([k] (rf/subscribe [::get-in [:current-route/transient k]]))
  ([k not-found] (rf/subscribe [::get-in [:current-route/transient k] not-found])))

(defn !route-get-in
  "A subscription for transient state storage, will be cleared on navigation."
  ([path] (rf/subscribe [::get-in (into [:current-route/transient] path)]))
  ([path not-found] (rf/subscribe [::get-in (into [:current-route/transient] path) not-found])))

(rf/reg-sub
 ::loading?
 (fn [_] [(!route-get-in [:fetch :loading])])
 (fn [[loading] _]
   (some? loading)))

(def !loading? (rf/subscribe [::loading?]))
(def !current-user (!get :current-user))
(def !current-route (!get ::current-route))
(def !current-route-fetch (!route-get :fetch))
(def !current-scan (!get ::current-scan))
(def !scan-mode (!get :scan/mode))
(def !make-names (!get :make/names))
(def !scan-active? (!get :scan/active? false))
(def !scan-tracker (!get :scan/tracker))
(def !current-compound (!get-in [:current-user :user/selected-compound]))

#_(deref !current-user)

(def !country-code->label
  (ratom/make-reaction (fn []
                         (update-vals @(!get :country/codes)
                                      @locale/!current-locale))))

(defn label->country-code [country-label]
  (get (clojure.set/map-invert @!country-code->label)
       country-label))

(comment
  @!country-code->label)



(defn merge-at [db key-or-path & args]
  (apply update-in
         db
         (if (keyword? key-or-path)
           [key-or-path]
           key-or-path)
         merge
         args))

(defn dissoc-in [db path]
  (update-in db (vec (drop-last path)) dissoc (last path)))

(defn patch-in [db path edits]
  (update-in db path (fnil editscript/patch {}) (editscript/edits->script edits)))

(defn upsert-in [db path v]
  (let [[k & rest] path
        db (if (nil? db)
             (if (= 0 k) [] {})
             db)]
    (if rest
      (assoc db k (upsert-in (get db k) rest v))
      (assoc db k v))))

(comment
  (upsert-in {:test [:foo :bar]} [:test 0] :baz)
  (upsert-in {:test [:foo :bar]} [:test 2] :baz)
  (upsert-in {:test {:foo :bar}} [:test :foo] :baz)
  (upsert-in {:test {:foo :bar}} [:test :bla] :baz))

(defn delete-in [db path]
  (let [disj' (fn [v k]
                (cond
                  (set? v) (disj v k)
                  (sequential? v) (into (empty v) (concat (take k v) (drop (inc k) v)))
                  :else (dissoc v k)))]
    (update-in db (vec (drop-last path)) disj' (last path))))

(comment
  (delete-in {:test [1 2]} [:test 0])
  (delete-in {:test #{1 2}} [:test 1])
  (delete-in {:test {:foo :bar :baz :saz}} [:test :foo]))

(defn op->fn [op]
  (case op
    :op/assoc assoc
    :op/assoc-in assoc-in
    :op/dissoc dissoc
    :op/dissoc-in dissoc-in
    :op/merge merge
    :op/merge-at merge-at
    :op/patch-in patch-in
    :op/upsert-in upsert-in
    :op/delete-in delete-in
    :op/update update
    :op/update-in (fn [m & args]
                    (let [[path f update-args] args]
                      (apply update-in m path f update-args)))))

(defn process-tx [db txs]
  (assert (map? db) "db must be map?")
  (assert (vector? txs) "txs must be vector?")
  (reduce (fn [db tx]
            (when-not (vector? tx)
              (throw (ex-info "unsupported tx" {:tx tx})))
            (apply (op->fn (first tx)) db (rest tx)))
          db
          txs))

(comment
  (process-tx {} [[:op/assoc-in [:foo :bar] :baz]])
  (process-tx {} [[:op/assoc :foo :bar]])
  (process-tx {:bar :one} [[:op/merge {:bar :baz :foo :bar}]])
  (process-tx {} [[:op/merge {:bar :baz}]])
  (process-tx {} [[:op/merge-at :foo {:bar :baz}]])
  (process-tx {} [[:op/merge-at [:foo :doo] {:bar :baz} {:ding :dong}]])
  (process-tx {:foo :bar :baz :bugs} [[:op/dissoc :foo :baz]])
  (process-tx {:foo {:bar :baz}} [[:op/dissoc-in [:foo :bar]]]))


(defn tx
  {::deframe/event true}
  [{:keys [db]} txs]
  {:db (process-tx db txs)})

(defn namespace-path-params
  "Temporary migration unnamespaced params until routes are using
  namespaced params."
  [path-params]
  (set/rename-keys path-params {:compound-name :compound/name}))

(defn parse-path-params [path-params]
  (cond-> path-params

    (contains? path-params :ductile/id)
    (update :ductile/id parse-uuid)))

(defn base64-encoded-params [path-params]
  (some-> path-params
          :ductile/base64-edn
          base64/decode
          edn/read-string))

(defn build-lambda-params [{:as match :keys [path-params]}]
  (merge (:query-params match)
         (base64-encoded-params path-params)
         (-> path-params
             namespace-path-params
             parse-path-params)))

(defn current-route-params [] (build-lambda-params @!current-route))

(defmulti route-on-load (fn [_ctx match]
                          (some-> match :data :on-load)))

(defmulti route-on-fetch (fn [match]
                           (some-> match :data :on-fetch)))

(def route-fetch-result-path
  [:current-route/transient :fetch])

(defn apply-current-route [db {:as match :keys [data path-params] ::keys [refetch?]}]
  (let [refetch? (or refetch? (:skip-loading-spinner? data))
        {:keys [on-load on-fetch fetch]} data
        on-fetch-lambda-opts (when on-fetch (route-on-fetch match))
        build-lambda-params-fn (:build-lambda-params on-fetch-lambda-opts build-lambda-params)]
    (cond-> {:db (cond-> (assoc db ::current-route match)
                   (:compound-name path-params)
                   (assoc-in [:current-user :user/selected-compound] (:compound-name path-params))
                   (not refetch?)
                   (dissoc :current-route/transient)
                   (not refetch?)
                   (assoc-in route-fetch-result-path (if fetch
                                                       {:loading true}
                                                       {:result match}))
                   (and (not refetch?) (contains? path-params :ductile/base64-edn))
                   (update :current-route/transient merge
                           (some-> path-params :ductile/base64-edn base64/decode edn/read-string)))}
      (not refetch?)
      (assoc `clear-interval {})
      on-load
      (route-on-load match)
      fetch
      (merge {::call-lambda [on-fetch-lambda-opts
                             fetch
                             (merge (build-lambda-params-fn match)
                                    (when refetch?
                                      (select-keys (get-in db (conj route-fetch-result-path :result)) [:ductile.lambda/diff-basis-t])))]}))))

(defn restore-localstorage [db]
  (assoc db ::localstorage
         (try (localstorage/get-item ::localstorage)
              (catch :default e
                (js/console.warn "Failed to restore ::deframe/localstorage" e)
                nil))))

(defn init-db
  {::deframe/event true}
  [_ init-db]
  (-> init-db
      (restore-localstorage)
      (apply-current-route (::current-route init-db))))

(defn dispatch!
  ([event-name] (dispatch! event-name {}))
  ([event-name opts]
   (rf/dispatch [event-name opts])))

(defn tx! [txs]
  (dispatch! `tx txs))

(defn assoc! [k v]
  (tx! [[:op/assoc k v]]))

(defn assoc-in! [path v]
  (tx! [[:op/assoc-in path v]]))

(defn dissoc! [& ks]
  (tx! [(into [:op/dissoc] ks)]))

(defn dissoc-in! [path]
  (tx! [[:op/dissoc-in path]]))

(defn upsert-in! [path v]
  (tx! [[:op/upsert-in path v]]))

(defn delete-in! [path]
  (tx! [[:op/delete-in path]]))

(defn route-assoc! [k v]
  (tx! [[:op/assoc-in [:current-route/transient k] v]]))

(defn route-assoc-in! [path v]
  (tx! [[:op/assoc-in (into [:current-route/transient] path) v]]))

(defn route-update! [k f & args]
  (tx! [[:op/update-in [:current-route/transient k] f args]]))

(defn route-update-in! [path f & args]
  (tx! [[:op/update-in (into [:current-route/transient] path) f args]]))

(defn route-dissoc! [k]
  (tx! [[:op/dissoc-in [:current-route/transient k]]]))

(defn route-dissoc-in! [path]
  (tx! [[:op/dissoc-in (into [:current-route/transient] path)]]))

(defn route-upsert-in! [path v]
  (tx! [[:op/upsert-in (into [:current-route/transient] path) v]]))

(defn route-delete-in! [path]
  (tx! [[:op/delete-in (into [:current-route/transient] path)]]))

(defn localstorage-assoc! [k v]
  (tx! [[:op/assoc-in [::localstorage k] v]]))

(defn init-db! [db]
  #_(js/console.log :init-db! db)
  (rf/dispatch-sync [`init-db db]))

(defn select-compound! [compound]
  #_(js/console.log :select-compound! compound)
  (tx! [[:op/assoc-in [:current-user :user/selected-compound] compound]]))

(defn scan! [scan]
  (js/console.log :scan! scan)
  (tx! [[:op/assoc ::current-scan (assoc scan :scan/timestamp (js/Date.))]]))

#_(scan! {:foo :bar})

(defn merge-db! [merged-db]
  (tx! [[:op/merge merged-db]]))

(defn reset-current-scan! []
  (tx! [[:op/dissoc ::current-scan]]))

#_(reset-current-scan!)

(defn reset-current-user! []
  (tx! [[:op/dissoc :current-user]]))

(defn set-scan-mode! [scan-mode]
  (tx! [[:op/assoc :scan/mode scan-mode]]))

#_(set-scan-mode! :vin)

(defn activate-scanner! []
  (tx! [[:op/assoc :scan/active? true]]))

(defn close-scanner! []
  (tx! [[:op/dissoc :scan/active?]]))

(defn validate-scan-tracker! [scan-tracker]
  (when-not (and (map? scan-tracker)
                 (fn? (:on-scan scan-tracker))
                 (some? (:view scan-tracker)))
    (throw (ex-info "scan-tracker needs to be a map with a `:on-scan` fn and a `:view`"
                    {:scan-tracker scan-tracker}))))

(defn set-scan-tracker! [scan-tracker]
  (validate-scan-tracker! scan-tracker)
  (tx! [[:op/assoc :scan/tracker scan-tracker]]))

#_(set-scan-tracker! {:on-scan js/console.log :view `debug})

(defn dispose-scan-tracker
  {::deframe/event true}
  [{:keys [db]} scan-tracker]
  (validate-scan-tracker! scan-tracker)
  #_(js/console.log ::dispose-scan-tracker {:view (:view scan-tracker) :view-matching? (= (get-in db [:scan/tracker :view]) (:view scan-tracker))})
  (if (and (= (get-in db [:scan/tracker :view]) (:view scan-tracker))
           (= (get-in db [:scan/tracker :on-scan]) (:on-scan scan-tracker)))
    {:db (dissoc db :scan/tracker)}
    {}))

(defn dispose-scan-tracker! [scan-tracker]
  (validate-scan-tracker! scan-tracker)
  (dispatch! `dispose-scan-tracker scan-tracker))

(defn route-path [k]
  [:current-route/transient k])

(defn promise-delay [v s]
  (-> (js/Promise. (fn [resolve] (js/setTimeout resolve (* 1000 s))))
      (.then (fn [_] v))))


(defonce !refetch-timeout
  (atom {}))

(declare call-lambda)

(defn call-route-refetch
  "Refetch current route with option for setting callbacks like `on-result` or `on-success`."
  ([] (call-route-refetch {}))
  ([opts] (call-lambda opts
                       (-> @!current-route :data :fetch)
                       (merge (build-lambda-params @!current-route)
                              (:extra-params opts)
                              (when-let [diff-basis-t @(!route-get-in [:fetch :result :ductile.lambda/diff-basis-t])]
                                {:ductile.lambda/diff-basis-t diff-basis-t})))))

(defn recurring-refetch [ms]
  (call-route-refetch
   {:on-result (fn [] (when (and (= "visible" js/document.visibilityState)
                                 (not= ::stopped (:timeout @!refetch-timeout)))
                        (reset! !refetch-timeout
                                {:ms ms
                                 :timeout (js/setTimeout #(recurring-refetch ms) ms)})))}))

(defn refetch-visibility-handler []
  ;; don't fetch updates when the page isn't visible
  (some-> @!refetch-timeout :timeout js/clearTimeout)
  (when (= "visible" js/document.visibilityState)
    (recurring-refetch (:ms @!refetch-timeout))))

(defn clear-interval
  {::deframe/effect true}
  [{:as opts}]
  (when-let [timeout (:timeout @!refetch-timeout)]
    (js/window.removeEventListener "visibilitychange" refetch-visibility-handler)
    (js/clearInterval timeout)
    (swap! !refetch-timeout assoc :timeout ::stopped)))

(defn lambda-loaded
  {::deframe/event true}
  [{:keys [db]} {:keys [sym lambda-params promise result-path on-result on-success result error]}]
  (if (= promise (get-in db (conj result-path :promise)))
    (let [success? (and (not error)
                        (not (contains? result :error)))]
      (cond
        (and success? on-success) (on-success result)
        on-result (on-result result)
        (not success?) (bugsnag/notify (or error
                                           (ex-info "lambda-loaded-error" (select-keys result [:error :error-message])))))

      (when-let [refetch-ms (:ductile.lambda/refetch-ms result)]
        (when (not (contains? (get-in db route-fetch-result-path) :result)) ;; first load, not a refetch
          (js/window.addEventListener "visibilitychange" refetch-visibility-handler)
          (reset! !refetch-timeout {:ms refetch-ms
                                    :timeout (js/setTimeout #(recurring-refetch refetch-ms)
                                                            refetch-ms)})))
      {:db (process-tx db (cond
                            result [[:op/dissoc-in (conj result-path :error) sym]
                                    [:op/dissoc-in (conj result-path :loading) sym]
                                    [:op/dissoc-in (conj result-path :promise) sym]
                                    (if success?
                                      (if (:ductile.lambda/diff result)
                                        [:op/patch-in (conj result-path :result) (:ductile.lambda/diff result)]
                                        [:op/assoc-in (conj result-path :result) result])
                                      [:op/assoc-in (conj result-path :error) result])]
                            error [[:op/assoc-in result-path {:error (if (contains? (ex-data error) :error)
                                                                       (ex-data error)
                                                                       {:error ::http/fetch-error :error-message (ex-message error)})}]]))})
    (do (js/console.warn "promise mismatch"
                         #js {:promises [promise (get-in db (conj result-path :promise))]
                              :sym sym
                              :lambda-params lambda-params
                              :result-path result-path})
        {})))

(defn call-lambda
  {::deframe/effect ::call-lambda}
  ([sym lambda-params]
   (call-lambda {} sym lambda-params))
  ([{:as opts :keys [result-path on-result on-success] :or {result-path route-fetch-result-path}} sym lambda-params]
   (when-not (vector? result-path)
     (throw (ex-info "needs result-path" {:result-path result-path :sym sym :lambda-params lambda-params})))
   (let [promise (http/$lambda sym lambda-params)
         loaded-params {:sym sym :lambda-params lambda-params :promise promise :result-path result-path :on-result on-result :on-success on-success}]
     (tx! [[:op/assoc-in (conj result-path :loading) sym]
           [:op/assoc-in (conj result-path :promise) promise]
           [:op/dissoc-in (conj result-path :error) sym]])
     (-> promise
         #_(promise-delay 0.5)
         (.then (fn [result]
                  (dispatch! `lambda-loaded (assoc loaded-params :result result))
                  result))
         (.catch (fn [error]
                   (dispatch! `lambda-loaded (assoc loaded-params :error error))))))))

(defn route-refetch
  {::deframe/event true}
  [{:as ctx :keys [db]} query-params]
  (if (get-in db (conj route-fetch-result-path :result))
    ;; don't refetch if the first fetch is still running
    (apply-current-route db (cond-> (assoc (::current-route db) ::refetch? true)
                              query-params (assoc :query-params query-params)))
    (js/console.log :skipping-refetch :initial-fetch-still-processing)))

(defn route-refetch!
  "Dispatch a route refetch event"
  ([]
   (dispatch! `route-refetch))
  ([query-params]
   (dispatch! `route-refetch query-params)))

(comment
  (route-refetch!))

(defn current-route-changed
  {::deframe/event true}
  [{:as ctx :keys [db]} match]
  (when (contains? db ::current-route)
    (when (:ductile.ui/reload-params db)
      (dispatch! `ductile.ui/attempt-reload (:ductile.ui/reload-params db)))
    (when-let [compound-name (get-in match [:path-params :compound-name])]
      (when (not= compound-name (get-in db [:current-user :user/selected-compound]))
        (http/$lambda 'ductile.lambda/handle-select-compound {:compound/name compound-name})))
    (apply-current-route db match)))

(defn perform-effects
  {::deframe/event true}
  [{:as ctx :keys [db]} effects]
  (update-vals effects vector))

(defn dispatch-effects! [effects]
  (dispatch! `perform-effects effects))

(defonce _persist-to-localstorage!
  (r/track!
   (fn []
     (when-let [m @(!get ::localstorage)]
       (localstorage/set-item! ::localstorage (pr-str m))))))
